import { useLazyQuery } from '@apollo/client';
import {
  ErrorMessage,
  Label,
  SelectDropdown,
  Typeahead,
} from '@gsa/afp-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { GET_SIN_OPTIONS } from '../../services/data-store.gql';
import { MODAL_MODES, useStandardItem } from './standard-item-provider';

const RadioGroup = ({ onChange }) => {
  return (
    <fieldset className="usa-fieldset" onChange={onChange}>
      <legend className="usa-legend usa-legend usa-sr-only">
        How would you like to apply the copied values
      </legend>
      <div className="usa-radio">
        <input
          data-testid="append-radio"
          className="usa-radio__input"
          id="append"
          type="radio"
          name="copyType"
          value="Append"
          aria-label="Append"
          aria-describedby="append-div"
        />
        <label className="usa-radio__label" htmlFor="append">
          Append
        </label>
      </div>
      <div className="usa-radio">
        <input
          data-testid="replace-radio"
          className="usa-radio__input"
          id="replace"
          type="radio"
          name="copyType"
          value="Replace"
          aria-label="Replace"
          aria-describedby="replace-div"
        />
        <label className="usa-radio__label" htmlFor="replace">
          Replace
        </label>
      </div>
    </fieldset>
  );
};

RadioGroup.propTypes = {
  onChange: PropTypes.func.isRequired,
};

let userTyped = false;
const AutoCompleteSelect = ({ onChange, value, getValues, program }) => {
  const [option, setOption] = useState({ values: [], raw: [] });

  const [getOptions] = useLazyQuery(GET_SIN_OPTIONS, {
    onCompleted: (data) => {
      let result = { values: [], raw: [] };
      if (data?.getStandardItemOptions) {
        result = {
          values: data.getStandardItemOptions.map((sin) => sin.value),
          raw: data.getStandardItemOptions,
        };
      }

      setOption(result);
    },
    onError: () => {},
  });

  AutoCompleteSelect.defaultProps = {
    value: '',
  };
  AutoCompleteSelect.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    getValues: PropTypes.func.isRequired,
    program: PropTypes.string.isRequired,
  };

  return (
    <div className="margin-top-1">
      <Typeahead
        name="Copy to Standard Item"
        required="true"
        role="textbox"
        data-testid="toStandardItemId"
        filterValue={value}
        typeaheadValues={option?.values}
        onFilterKeyDown={() => {
          userTyped = true;
        }}
        onOptionEnter={(selected) => {
          const selectedData = option?.raw.find((o) => o.value === selected);
          onChange({ selected, selectedData });
        }}
        fetchTypeaheadValues={(_, search) => {
          const keyword = search.trim();
          if (userTyped) {
            getOptions({
              variables: {
                searchTerm: keyword,
                year: Number(getValues('copyToYear')),
                program,
              },
            });

            userTyped = false;
          }
        }}
        debounceDelay={500}
        inputCharNum={0}
        onClear={(selected) => {
          onChange({ selected, undefined });
        }}
      />
    </div>
  );
};

const formSchema = yup.object().shape({
  toStandardItemId: yup.string().required('Copy to Standard Item required'),
  copyFromYear: yup.string().required('Copy from year is required'),
  copyToYear: yup.string().required('Copy to year is required'),
  copyType: yup.string().required('Copy type is required'),
});

const StandardAssociationCopy = () => {
  const {
    standardItem,
    multipleOptions,
    selectedAssociations,
    setData,
  } = useStandardItem();

  const [toStandardItem, setToStandardItem] = useState({});

  const {
    formState: { errors },
    control,
    handleSubmit,
    register,
    getValues,
  } = useForm({
    resolver: yupResolver(formSchema),
  });

  const onSubmit = (data) => {
    // Prep data.
    const copyInput = {
      toStandardItem,
      fromStandardItemId: Number(standardItem.id),
      toStandardItemId: Number(toStandardItem?.id),
      copyFromYear: Number(data.copyFromYear),
      copyToYear: Number(data.copyToYear),
      associationsToCopy: selectedAssociations?.map((a) =>
        Number(a?.original?.id),
      ),
      copyType: data.copyType.toLowerCase(),
    };

    setData('SET_EQUIPMENT_ASSOCIATION_COPY', copyInput);
    if (data.copyType.toLowerCase() === 'append') {
      setData('SET_MODAL_MODE', MODAL_MODES.CONFIRM_APPEND);
    } else {
      setData('SET_MODAL_MODE', MODAL_MODES.CONFIRM_COPY);
    }
    setData('SET_FORM_MODAL', true);
  };

  return (
    <form
      id="equipment-code-copy-form"
      className="usa-form usa-form--large"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div className="text-bold margin-bottom-1">Copy from Standard Item</div>
      <div>
        {`${standardItem?.standardItemNumber} - ${standardItem?.title} - ${standardItem?.year}`}
      </div>
      <input
        id="fromStandardItemId"
        data-testid="fromStandardItemId"
        {...register('fromStandardItemId')}
        type="hidden"
        defaultValue={standardItem?.standardItemNumber ?? ''} />
      <input
        id="copyFromYear"
        data-testid="copyFromYear"
        {...register('copyFromYear')}
        type="hidden"
        defaultValue={standardItem?.year ?? ''} />

      <Label htmlFor="year" className="text-bold">
        Copy to year&nbsp;
        <abbr title="required" className="usa-hint usa-hint--required">
          *
        </abbr>
      </Label>
      <Controller
        id="copyToYear"
        name="copyToYear"
        control={control}
        defaultValue={
          multipleOptions.find((o) => o.currentYear === true)?.value ?? ''
        }
        render={({ field: { onChange, value } }) => (
          <SelectDropdown
            data-testid="copyToYear"
            aria-label="Copy to year"
            aria-required="true"
            value={
              value ??
              multipleOptions.find((o) => o.currentYear === true)?.value
            }
            options={multipleOptions?.filter(
              (o) =>
                o?.type === 'year' &&
                o?.uniqueKey === 'year' &&
                o.value >=
                  multipleOptions.find((c) => c.currentYear === true)?.value,
            )}
            onChange={(e) => {
              onChange(e.target.value);
            }}
          />
        )}
      />
      {errors?.copyToYear?.message && (
        <ErrorMessage>{errors.copyToYear?.message}</ErrorMessage>
      )}

      <Label htmlFor="toStandardItemId" className="text-bold">
        Copy to Standard Item&nbsp;
        <abbr title="required" className="usa-hint usa-hint--required">
          *
        </abbr>
      </Label>
      <Controller
        control={control}
        name="toStandardItemId"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <AutoCompleteSelect
            value={value ?? ''}
            getValues={getValues}
            program={standardItem?.program}
            onChange={({ selected, selectedData }) => {
              onChange(selected);
              setToStandardItem(selectedData);
            }}
          />
        )}
      />
      {errors?.toStandardItemId?.message && (
        <ErrorMessage>{errors.toStandardItemId?.message}</ErrorMessage>
      )}

      <Label htmlFor="toStandardItemId" className="text-bold">
        How would you like to apply the copied values?&nbsp;
        <abbr title="required" className="usa-hint usa-hint--required">
          *
        </abbr>
      </Label>
      <Controller
        control={control}
        name="copyType"
        defaultValue=""
        render={({ field: { onChange, value } }) => (
          <RadioGroup
            data-testid="copyType"
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
            }}
          />
        )}
      />
      {errors?.copyType?.message && (
        <ErrorMessage>{errors.copyType?.message}</ErrorMessage>
      )}
      <br />
      <div className="text-bold" id="replace-div">
        Replace
      </div>
      <div className="text-italic">
        All options will be copied. Existing options will be overwritten.
      </div>
      <br />
      <div className="text-bold" id="append-div">
        Append
      </div>
      <div className="text-italic">
        Any option not found in the COPY TO Standard Item will be appended.
      </div>
    </form>
  );
};

export default StandardAssociationCopy;
